import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Custom Hook für Overlay-Scroll-Funktionalität mit "Read more" Button.
 *
 * Bietet:
 * - Scroll-Erkennung (ob Inhalt scrollbar ist)
 * - "At bottom" Erkennung (ob am Ende gescrollt wurde)
 * - Scroll-Button Sichtbarkeit
 * - handleScrollMore Funktion (scrollt 75% nach unten)
 *
 * @param deps - Abhängigkeiten, die einen Re-Check auslösen sollen (z.B. wenn sich Overlay-Inhalt ändert)
 * @returns { bodyRef, showScrollButton, isAtBottom, handleScrollMore }
 *
 * @example
 * const { bodyRef, showScrollButton, isAtBottom, handleScrollMore } = useOverlayScroll([contentState]);
 *
 * <div ref={bodyRef} className="Overlay-body">...</div>
 * <div className={`Overlay-footer ${isAtBottom ? 'at-bottom' : ''}`}>
 *   {showScrollButton && <button onClick={handleScrollMore}>Read more</button>}
 * </div>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
export function useOverlayScroll(deps: any[] = []) {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const checkScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const { scrollTop, scrollHeight, clientHeight } = body;
    const hasScroll = scrollHeight > clientHeight + 5;
    const atBottom = scrollHeight - scrollTop - clientHeight < 10;
    setShowScrollButton(hasScroll && !atBottom);
    setIsAtBottom(atBottom || !hasScroll);
  }, []);

  const handleScrollMore = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const scrollAmount = body.clientHeight * 0.75;
    body.scrollBy({ top: scrollAmount, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const timer = setTimeout(checkScroll, 100);
    const body = bodyRef.current;
    if (!body) return;

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(body);
    body.addEventListener("scroll", checkScroll);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      body.removeEventListener("scroll", checkScroll);
    };
  }, [checkScroll, ...deps]);

  return {
    bodyRef,
    showScrollButton,
    isAtBottom,
    handleScrollMore,
  };
}
